import { ACCESS_POLICY } from 'constants/constants';
import { useThreeBProductType } from 'hooks/isThreeBProduct';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useFormatting } from 'locale';
import { useManagerProfile } from 'prodivers/managerProfile';
import {
    useCallback, useEffect, useMemo, useRef, useState, ChangeEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { memberIdSelector } from 'redux-store/auth/authSelectors';
import { adaptAbisory } from '../adapters/advisor';
import { isValid, validate } from '../valdation';

// --- Interfaces ---

interface BaseAgentInformationProps {
    newPortfolio: any;
}

interface AdvisorOption {
    label: string;
    value: string;
}

interface GeneralAgency {
    id: string | number;
    name: string;
    [key: string]: any;
}

// Pure utility — no hook deps, lives outside component
const formatPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber) return '-';

    return phoneNumber.indexOf('|') > -1 ? `+${phoneNumber.split('|').join(' ')}` : phoneNumber;
};

export const useBaseAgentInformation = ({ newPortfolio }: BaseAgentInformationProps) => {
    const { t } = useTranslation();
    const { hasAccessPolicy, isPolicyLoading } = useManagerProfile();
    const { getFormattedNumber } = useFormatting();
    const memberId = useSelector(memberIdSelector);

    // --- State ---
    const [issueCommissionSelected, setIssueCommissionSelected] = useState<string | number | null>(null);
    const [pbSelected, setPBSelected] = useState<string>('');
    const [selectedGeneralAgency, setSelectedGeneralAgency] = useState<string | number | null>(null);
    const [vstNumber, setVSTNumber] = useState<string>('');
    const [advisorData, setAdvisorData] = useState<AdvisorOption[]>([]);
    const [isContinuesValidation, setContinuesValidation] = useState<boolean>(false);

    // --- Access Policies ---
    const isAdvisorCanBeSelected = hasAccessPolicy(ACCESS_POLICY.EnableAdvisorAssignment);
    const isManualPBRestricted = !hasAccessPolicy(ACCESS_POLICY.EnablePBNumberSelection);

    const {
        isZic, isPk, isAzp, isZivZifd,
    } = useThreeBProductType(newPortfolio);

    const { data: generalAgencies } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getGeneralAgencies',
        enabled: true,
        staleTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const { data: memberProfileResponse, isLoading: isMemberProfileLoading } = useGetCustomService({
        service: 'member',
        api: 'getMembersProfile',
        payload: memberId,
        enabled: !!memberId,
        staleTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const initialAgent = isAdvisorCanBeSelected ? newPortfolio?.applicationData?.memberId || memberId : memberId;
    const [selectedAgent, setSelectedAgent] = useState<string | number | null>(initialAgent || null);
    const [agentFilter, setAgentFilter] = useState<string>('');

    const { data: membersData, isLoading: searchAgentsInProgress } = useGetCustomService({
        service: 'customMembersService',
        api: 'getMembers',
        payload: { filter: agentFilter, currentId: selectedAgent || initialAgent },
        enabled: true,
        staleTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (selectedAgent === null) {
            setSelectedAgent(initialAgent);
        } else {
            setAgentFilter('');
        }
    }, [initialAgent, selectedAgent]);

    // --- Validation Logic ---
    const validationResult = useMemo(() => (
        validate({
            commission: issueCommissionSelected,
            pbNumber: pbSelected,
            vstNumber,
            generalAgency: selectedGeneralAgency,
            agentInfo: selectedAgent,
            isZic,
        }, t)
    ), [issueCommissionSelected, pbSelected, vstNumber, selectedGeneralAgency, selectedAgent, isZic, t]);

    const isValidFormData = isValid(validationResult);

    const validation = useMemo(() => (isContinuesValidation ? validationResult : {}), [isContinuesValidation, validationResult]);

    const modelPortfolioSettings = useMemo(() => {
        try {
            return JSON.parse(newPortfolio?.modelPortfolio?.Attributes ?? '{}');
        } catch (e) {
            return {};
        }
    }, [newPortfolio?.modelPortfolio?.Attributes]);

    // --- Commission Logic ---
    const getFilteredIssueCommission = useCallback((commission: number | string) => {
        const investmentAmount = newPortfolio?.applicationData?.investmentDescription?.productConfiguration?.initialInvestment;
        const commValue = typeof commission === 'string' ? parseFloat(commission) : commission;

        if (isAzp || isZivZifd) {
            if (investmentAmount > 2000000) return commValue <= 1;
            if (investmentAmount >= 1000000.01) return commValue <= 1.5;
            if (investmentAmount >= 500000.01) return commValue <= 2;
            if (investmentAmount >= 250000.01) return commValue <= 3;
        }

        return !!commission?.toString();
    }, [newPortfolio?.applicationData?.investmentDescription?.productConfiguration?.initialInvestment, isAzp, isZivZifd]);

    const issueCommissionOptions = useMemo(() => (
        ((modelPortfolioSettings?.issueComission as any[]) ?? [{ label: '0.0', value: '0' }])
            ?.filter((item) => getFilteredIssueCommission(item?.value))
            ?.map((item) => ({
                label: getFormattedNumber(item?.label, { maximumFractionDigits: 1, minimumFractionDigits: 1 }),
                value: String(item.value),
            }))
            .sort((a, b) => parseFloat(b.value) - parseFloat(a.value))
    ), [modelPortfolioSettings?.issueComission, getFilteredIssueCommission, getFormattedNumber]);

    // --- API Interactions ---
    const getAgentInformation = (filter: string = '') => setAgentFilter(filter);

    const changeVSTNumber = useCallback((e: ChangeEvent<HTMLInputElement> | string) => {
        if (typeof e === 'string') {
            setVSTNumber(e);
        } else {
            setVSTNumber(e?.target?.value || '');
        }
    }, []);

    // --- Initialization Effects ---
    useEffect(() => {
        const agentInformation = newPortfolio?.applicationData?.investmentDescription?.agentInformation ?? newPortfolio?.agentInformation;

        if (agentInformation) {
            setVSTNumber(agentInformation?.vstNumber || '');
            if (issueCommissionOptions?.length) {
                const selectIssueCommissionCandidate = String(agentInformation?.issueCommission);

                if (issueCommissionOptions.find((item) => (item?.value === selectIssueCommissionCandidate))) {
                    setIssueCommissionSelected(selectIssueCommissionCandidate);
                }
            }

            setPBSelected(agentInformation?.pbNumber ?? agentInformation?.pbSelected ?? '');
            if (generalAgencies?.data?.length > 0) {
                setSelectedGeneralAgency((agentInformation?.agencyId ?? agentInformation?.selectedGeneralAgency) || null);
            }
        }
    }, [
        newPortfolio?.applicationData?.investmentDescription?.agentInformation,
        newPortfolio?.agentInformation,
        issueCommissionOptions,
        generalAgencies?.data,
    ]);

    // Set 0 commission for specific conditions
    useEffect(() => {
        if ((newPortfolio?.applicationData?.investmentDescription?.personalDetails?.zurichEmployee || isPk) && issueCommissionOptions) {
            const zeroCommission = issueCommissionOptions?.find(f => f.label === '0.0' || f.value === '0')?.value || '0';

            setIssueCommissionSelected(zeroCommission);
        }
    }, [issueCommissionOptions, newPortfolio?.applicationData?.investmentDescription?.personalDetails?.zurichEmployee, isPk]);

    const hasFetchedInitial = useRef(false);

    useEffect(() => {
        if (!hasFetchedInitial.current && memberId) {
            hasFetchedInitial.current = true;
            getAgentInformation('');
        }
    }, [memberId, getAgentInformation]);

    useEffect(() => {
        if (memberProfileResponse?.data) {
            setAdvisorData(adaptAbisory(memberProfileResponse.data, t));
        }
    }, [memberProfileResponse?.data, t]);

    return {
        t,
        isManualPBRestricted,
        isAdvisorCanBeSelected,
        setContinuesValidation,
        validation,
        isValid: isValidFormData,
        formatPhoneNumber,
        productDesc: newPortfolio?.product?.description,
        isLoading: isMemberProfileLoading || isPolicyLoading,
        issueCommissionSelected,
        issueCommissionChange: setIssueCommissionSelected,
        issueCommissionOptions,
        pbSelected,
        pbChange: setPBSelected,
        advisorData,
        vstNumber,
        changeVSTNumber,
        generalAgencies: (generalAgencies?.data as GeneralAgency[]) || [],
        selectedGeneralAgency,
        setSelectedGeneralAgency,
        agentsList: membersData?.data || [],
        selectedAgent,
        setSelectedAgent,
        handleSearchAgents: getAgentInformation,
        searchAgentsInProgress,
    };
};
